import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import gridHorizontalAsset from "@/assets/loader/loader-grid-horizontal.svg";
import gridVerticalAsset from "@/assets/loader/loader-grid-vertical.svg";
import watermarkAsset from "@/assets/loader/loader-brand-watermark.svg";
import { FullPageLoaderSpinner } from "./FullPageLoaderSpinner";

const loaderAssets = {
  gridHorizontal: gridHorizontalAsset,
  gridVertical: gridVerticalAsset,
  watermark: watermarkAsset
} as const;

export type FullPageLoaderProps = HTMLAttributes<HTMLDivElement> & {
  titlePrefix?: ReactNode;
  titleHighlight?: ReactNode;
  titleSuffix?: ReactNode;
  description?: ReactNode;
  fullScreen?: boolean;
  statusLabel?: string;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const FullPageLoaderRoot = styled("div", {
  label: "AgenticFullPageLoader-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { fullScreen: boolean } }>(({ ownerState }) => [
  {
    position: "relative",
    isolation: "isolate",
    display: "flex",
    overflow: "hidden",
    borderRadius: "var(--bfrs-radius-lg)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.fullScreen ? { minHeight: "100dvh" } : { minHeight: "520px" }
]);

const decorBase = {
  pointerEvents: "none",
  position: "absolute",
  zIndex: 0,
  maxWidth: "none",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  userSelect: "none",
  OObjectFit: "fill",
  objectFit: "fill"
} as const;

const FullPageLoaderGridHorizontal = styled("img", {
  label: "AgenticFullPageLoader-gridHorizontal"
})({
  ...decorBase,
  left: "3%",
  top: 0,
  height: "95%",
  width: "92%"
});

const FullPageLoaderGridVertical = styled("img", {
  label: "AgenticFullPageLoader-gridVertical"
})({
  ...decorBase,
  left: 0,
  top: "3.5%",
  height: "98%",
  width: "100%"
});

const FullPageLoaderWatermark = styled("img", {
  label: "AgenticFullPageLoader-watermark"
})({
  ...decorBase,
  bottom: "-52%",
  left: "-5%",
  height: "113%",
  width: "68%"
});

const FullPageLoaderContent = styled("div", {
  label: "AgenticFullPageLoader-content"
})({
  position: "relative",
  zIndex: 10,
  marginLeft: "auto",
  marginRight: "auto",
  display: "flex",
  width: "100%",
  maxWidth: "56rem",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "5rem",
  paddingBottom: "5rem",
  textAlign: "center"
});

const FullPageLoaderTitle = styled("h1", {
  label: "AgenticFullPageLoader-title"
})({
  maxWidth: "720px",
  textWrap: "balance",
  fontSize: "1.875rem",
  fontWeight: 700,
  lineHeight: "1.12",
  letterSpacing: "0em",
  "@media (min-width:640px)": {
    fontSize: "2.25rem",
    lineHeight: "2.5rem"
  }
});

const FullPageLoaderHighlight = styled("span", {
  label: "AgenticFullPageLoader-highlight"
})({
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary-bright) / var(--tw-text-opacity, 1))"
});

const FullPageLoaderDescription = styled("p", {
  label: "AgenticFullPageLoader-description"
})({
  marginTop: "1rem",
  fontSize: "1rem",
  lineHeight: "1.75rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  "@media (min-width:640px)": {
    fontSize: "1.25rem",
    lineHeight: "1.75rem"
  }
});

export const FullPageLoader = forwardRef<HTMLDivElement, FullPageLoaderProps>(
  (
    {
      className,
      titlePrefix = "Your account is ready. I'll take you to your ",
      titleHighlight = "dashboard",
      titleSuffix = " now.",
      description = "Your account is being configured. this takes just a moment",
      fullScreen = true,
      statusLabel = "Loading account dashboard",
      ...props
    },
    ref
  ) => (
    <FullPageLoaderRoot
      ref={ref}
      role="status"
      aria-busy="true"
      aria-label={statusLabel}
      ownerState={{ fullScreen: Boolean(fullScreen) }}
      className={className}
      {...props}
    >
      <FullPageLoaderGridHorizontal src={loaderAssets.gridHorizontal} alt="" aria-hidden="true" />
      <FullPageLoaderGridVertical src={loaderAssets.gridVertical} alt="" aria-hidden="true" />
      <FullPageLoaderWatermark src={loaderAssets.watermark} alt="" aria-hidden="true" />

      <FullPageLoaderContent>
        <FullPageLoaderSpinner />
        <FullPageLoaderTitle>
          {titlePrefix}
          <FullPageLoaderHighlight>{titleHighlight}</FullPageLoaderHighlight>
          {titleSuffix}
        </FullPageLoaderTitle>
        {description && <FullPageLoaderDescription>{description}</FullPageLoaderDescription>}
      </FullPageLoaderContent>
    </FullPageLoaderRoot>
  )
);

FullPageLoader.displayName = "FullPageLoader";
